import {
  ChevronUpIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useId, useRef, useState } from 'react';

import { BANNER_REASONS, ReasonIcon } from './FillReasons';

import { trackSmartFillUsed } from '@/util/vexo';

// "Why shifts are not filling" (GRAVY-10888 round 2 V4; Figma why-banner,
// 42087:23463): a count per reason over the week's shifts, each one a filter.
// A shift counts once for every reason its card shows, so the counts can add
// up to more than the shifts — that is the design's own example (13 for 7).
//
// The counts are the server's `totals.fill_reasons`, narrowed by every filter
// except the reason filter itself, like the Critical / At risk pills; so a
// selected chip keeps showing what switching would find. Absent (not zero) for
// a viewer who doesn't see Smart Fill: the banner then renders nothing.

type Props = {
  counts?: Record<string, number> | null;
  value: string[];
  onChange: (next: string[]) => void;
  /** Clearing the only chip unmounts the banner itself: the caller moves focus. */
  onEmptied?: () => void;
};

const WhyNotFillingBanner = ({ counts, value, onChange, onEmptied }: Props) => {
  // On a phone the banner folds away behind its title (Figma mobile frame,
  // 42087:19059); the toggle only renders at that width (CSS).
  const [open, setOpen] = useState(true);
  const chipsId = useId();
  const bannerRef = useRef<HTMLElement>(null);
  if (!counts) return null;
  const chips = BANNER_REASONS.filter(
    (r) => (counts[r.code] ?? 0) > 0 || value.includes(r.code),
  );
  if (chips.length === 0) return null;

  const toggle = (code: string) => {
    const clearing = value.includes(code);
    // A 0-count chip renders only while selected, so clearing it unmounts the
    // focused button and focus would fall to <body>; keep it in the banner
    // (review N-08).
    // When it was the only chip the banner goes too (review N-08, edge).
    if (clearing && !(counts[code] ?? 0)) {
      if (chips.length === 1 && onEmptied) onEmptied();
      else bannerRef.current?.focus();
    }
    // Analytics (smart_fill_used): picking a reason, not clearing one.
    if (!clearing)
      trackSmartFillUsed({ action: 'reason_filter', reason: code });
    onChange(clearing ? value.filter((c) => c !== code) : [...value, code]);
  };

  return (
    <section
      className={`dashv2-whybanner${open ? '' : ' is-collapsed'}`}
      data-testid="fillReasonBanner"
      aria-label="Why shifts are not filling"
      ref={bannerRef}
      tabIndex={-1}
    >
      <div className="dashv2-whybanner-title">
        <ExclamationCircleIcon aria-hidden="true" />
        Why shifts are not filling
        <button
          type="button"
          className="dashv2-whybanner-toggle"
          aria-expanded={open}
          aria-controls={chipsId}
          aria-label={open ? 'Hide reasons' : 'Show reasons'}
          onClick={() => setOpen((o) => !o)}
        >
          <ChevronUpIcon aria-hidden="true" />
        </button>
      </div>
      <div className="dashv2-whybanner-chips" id={chipsId}>
        {chips.map((r) => {
          const selected = value.includes(r.code);
          return (
            <button
              key={r.code}
              type="button"
              className={`dashv2-whychip${selected ? ' is-selected' : ''}`}
              data-testid="fillReasonChip"
              data-reason={r.code}
              aria-pressed={selected}
              title={selected ? 'Clear filter' : `Show only ${r.label} shifts`}
              onClick={() => toggle(r.code)}
            >
              <ReasonIcon code={r.code} size={18} />
              <span className="dashv2-whychip-label">{r.label}</span>
              <span className="dashv2-whychip-count">
                ({counts[r.code] ?? 0})
              </span>
              {selected && (
                <XMarkIcon
                  className="dashv2-whychip-clear"
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default WhyNotFillingBanner;
